import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { StatusPill } from "@/components/inst/ProductImage";
import { SpecPdfButton } from "@/components/inst/SpecPdfButton";
import { Loader2, ChevronDown, ChevronUp, Mail, Save } from "lucide-react";
import { toast } from "sonner";

export function InstRequests() {
  const [rows, setRows] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/inst/requests").then(({ data }) => { setRows(data.requests || []); setStatuses(data.statuses || []); }).finally(() => setLoading(false));
  }, []);

  const update = async (id, patch) => {
    try {
      const { data } = await api.patch(`/inst/models/${id}`, patch);
      setRows((rs) => rs.map((r) => (r.id === id ? data.model : r)));
      toast.success("Request updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const shown = rows.filter((r) => filter === "all" || (filter === "open" ? !["Quoted", "Closed"].includes(r.status) : r.status === filter));

  return (
    <div>
      <NotifySettings />
      <div className="flex flex-wrap gap-2 mb-4">
        {["open", "all", ...statuses.filter((s) => s !== "Draft")].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`inst-req-filter-${s.replace(/ /g, "-").toLowerCase()}`}
            className={`px-3 py-1.5 text-xs border capitalize transition-colors ${filter === s ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-zinc-300 text-zinc-600 hover:border-[#0F172A]"}`}>{s}</button>
        ))}
      </div>
      <div className="bg-white border border-zinc-200 divide-y divide-zinc-100" data-testid="inst-requests-list">
        {loading && <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>}
        {!loading && shown.length === 0 && <div className="py-12 text-center text-sm text-zinc-400" data-testid="inst-requests-empty">No quote requests here.</div>}
        {shown.map((r) => (
          <div key={r.id} data-testid={`inst-request-${r.id}`}>
            <button onClick={() => setOpen(open === r.id ? null : r.id)} className="w-full p-4 flex flex-wrap items-center gap-3 text-left hover:bg-zinc-50" data-testid={`inst-request-toggle-${r.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="font-medium text-[#0F172A]">{r.company_name} <span className="text-zinc-400 font-normal">· {r.name}</span></div>
                <div className="text-xs text-zinc-500">{r.product_name} · Qty {r.quantity || 1} · {(r.quote_requested_at || "").slice(0, 10)}</div>
                {r.model_number && <div className="mt-1 font-mono text-sm text-[#0F172A] break-all" data-testid={`inst-request-model-number-${r.id}`}>{r.model_number}</div>}
              </div>
              <StatusPill status={r.status} testid={`inst-request-status-${r.id}`} />
              {open === r.id ? <ChevronUp className="h-4 w-4 text-zinc-400" /> : <ChevronDown className="h-4 w-4 text-zinc-400" />}
            </button>
            {open === r.id && <RequestDetail r={r} statuses={statuses} onUpdate={(p) => update(r.id, p)} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function RequestDetail({ r, statuses, onUpdate }) {
  const [notes, setNotes] = useState(r.staff_notes || "");
  return (
    <div className="px-4 pb-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid={`inst-request-selections-${r.id}`}>
          {Object.entries(r.selections || {}).map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 border-b border-zinc-100 py-1.5"><span className="text-zinc-500">{k}</span><span className="font-medium text-right">{v}</span></div>
          ))}
        </div>
        {r.customer_notes && <p className="mt-3 text-sm text-zinc-600"><span className="text-zinc-400">Customer notes:</span> {r.customer_notes}</p>}
        <a href={`mailto:${r.email}?subject=${encodeURIComponent(`Quote: ${r.product_name} — ${r.name}`)}`} className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#0F172A] hover:text-[#B8860B]" data-testid={`inst-request-email-${r.id}`}><Mail className="h-3.5 w-3.5" /> {r.contact_name} · {r.email}</a>
        <SpecPdfButton model={r} testid={`inst-request-pdf-${r.id}`} className="mt-2 text-xs text-[#0F172A] hover:text-[#B8860B]" />
      </div>
      <div className="space-y-3">
        <label className="block text-xs text-zinc-500">Status
          <select value={r.status} onChange={(e) => onUpdate({ status: e.target.value })} data-testid={`inst-request-status-select-${r.id}`} className="mt-1 w-full px-3 py-2 border border-zinc-300 bg-white text-sm">
            {statuses.filter((s) => s !== "Draft").map((s) => <option key={s}>{s}</option>)}
          </select>
        </label>
        <label className="block text-xs text-zinc-500">Internal notes
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`inst-request-notes-${r.id}`} className="mt-1 w-full px-3 py-2 border border-zinc-300 text-sm" />
        </label>
        <button onClick={() => onUpdate({ staff_notes: notes })} data-testid={`inst-request-save-notes-${r.id}`} className="flex items-center gap-1.5 px-3 py-2 text-xs bg-[#0F172A] text-white"><Save className="h-3.5 w-3.5" /> Save notes</button>
      </div>
    </div>
  );
}

function NotifySettings() {
  const [val, setVal] = useState("");
  const [def, setDef] = useState("");
  useEffect(() => { api.get("/inst/settings").then(({ data }) => { setVal(data.notify_emails || ""); setDef(data.default_email || ""); }).catch(() => {}); }, []);
  const save = async () => {
    try { await api.patch("/inst/settings", { notify_emails: val }); toast.success("Alert recipients saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-zinc-200 p-4 mb-5 flex flex-wrap items-end gap-3" data-testid="inst-notify-settings">
      <label className="flex-1 min-w-[240px] text-xs text-zinc-500">Email alerts for new quote requests go to (comma-separated)
        <input value={val} onChange={(e) => setVal(e.target.value)} placeholder={def || "sales@yourcompany.com"} data-testid="inst-notify-emails" className="mt-1 w-full px-3 py-2 border border-zinc-300 text-sm font-mono" />
      </label>
      <button onClick={save} data-testid="inst-notify-save" className="px-4 py-2 text-sm bg-[#0F172A] text-white">Save</button>
    </div>
  );
}
